import useUserPersonalData from '@apis/queries/useUserPersonalData';
import { APP_FIRST_SUBPAGES, APP_SUB_MENUS, NAVBAR_LINKS } from '@utils/variables';
import { useRouter } from 'next/router';
import React, { FC, ReactNode, useCallback, useMemo, useState } from 'react';
import NeutralButton from 'src/components/buttons/NeutralButton';
import AnimatedArrow from 'src/components/common/AnimatedArrow';
import Collapse from 'src/components/common/Collapse';
import ItemButton from 'src/components/common/ItemButton';
import { StyledNavLinksList } from 'src/components/navbar/Navbar/Navbar.styles';
import NavbarAvatar from 'src/components/navbar/NavbarAvatar';
import { pushURL } from 'src/functions/functions';
import useResponsive from 'src/hooks/contexts/useResponsive';
import useCurrentPageId from 'src/store/appState/hooks/useCurrentPageId';
import useCurrentSubpage from 'src/store/appState/hooks/useCurrentSubpage';
import Flex from 'src/styles/layouts/Flex';
import { EFlex } from 'src/styles/layouts/Flex/Flex.enum';
import NGHeading from 'src/styles/theme/components/NGHeading';
import NGIcon from 'src/styles/theme/components/NGIcon';
import NGText from 'src/styles/theme/components/NGText';
import { EFontFamily, ETextAlign, ETextTransform, ETextType } from 'src/styles/theme/components/NGText/NGText.enum';
import { EMediaQuery, ESize, ETextColor, EThemeColor } from 'src/styles/theme/utils/enums';
import { IAppLink } from '../../../types/global';
import Sublinks from '../Sublinks';
import { ISubmenu } from '../Sublinks/Sublinks.type';
import {
	FlexEx,
	StyledBorderContainer,
	StyledMobileListItem,
	StyledNavLinksListItem,
	StyledSublinkList,
	StyledSublinks
} from './Mainlinks.styles';
import { IMainlinks } from './Mainlinks.type';

const Mainlinks: FC<IMainlinks> = ({ activeId, onClick, isHidden }) => {
	const [currentPageId] = useCurrentPageId();
	const [{ id: currentSubpageId }] = useCurrentSubpage();

	const { isSmallerThanMd } = useResponsive();
	const { username } = useUserPersonalData();

	const router = useRouter();
	const { pathname } = router;

	const [mobileOpenId, setMobileOpenId] = useState<string>('');

	const handleClick = useCallback(
		(href: string) => {
			if (pathname === href) {
				const selectedPage = NAVBAR_LINKS.find(({ id }: IAppLink) => id === activeId);
				const submenusList = selectedPage?.submenus || [];

				if (selectedPage && (APP_FIRST_SUBPAGES.includes(currentSubpageId) || !currentSubpageId)) {
					return;
				}

				if (!!selectedPage?.href && submenusList.length) {
					pushURL(selectedPage?.href, submenusList[0].id);
				}

				onClick && onClick();
				return;
			}

			router.push(href);
			onClick && onClick();
		},
		[pathname, router, onClick, currentSubpageId, activeId]
	);

	const handleSubmenuClick = useCallback(
		(href: string, submenuId: string) => {
			if (
				href === pathname &&
				APP_FIRST_SUBPAGES.includes(submenuId) &&
				(APP_FIRST_SUBPAGES.includes(currentSubpageId) || !currentSubpageId)
			) {
				return;
			}
			pushURL(href, submenuId);
		},
		[currentSubpageId, pathname]
	);

	// same code as the one in <Sublinks /> => @todo(refactor)
	const forceQuestActivated = useMemo(
		() => !!router?.pathname && router.pathname.includes('hub/quests/'),
		[router.pathname]
	);

	// same code as the one in <Sublinks /> => @todo(refactor)
	const getIconColor = useCallback(
		(
			id: string,
			pageId: string
		):
			| {
					text: ETextColor;
					theme: EThemeColor;
			  }
			| undefined => {
			if (
				(forceQuestActivated && id === APP_SUB_MENUS.devhub.quests.id) ||
				(!forceQuestActivated &&
					pageId === currentPageId &&
					(currentSubpageId === id || (!currentSubpageId && APP_FIRST_SUBPAGES.includes(id))))
			) {
				return {
					text: ETextColor.white,
					theme: EThemeColor.white
				};
			}

			return undefined;
		},
		[currentPageId, currentSubpageId, forceQuestActivated]
	);

	return (
		<StyledNavLinksList isHidden={isHidden}>
			{NAVBAR_LINKS.filter(
				({ isHidden = false, isMobileVisible = false }: IAppLink) => !isHidden || (isSmallerThanMd && isMobileVisible)
			).map(({ href, label, id }: IAppLink): React.ReactNode => {
				const isDisabled = false; //(APP_LINKS.home.href === href || APP_LINKS.staking.href === href) && !isSuperUser;

				return isSmallerThanMd ? (
					<StyledMobileListItem
						key={href + '-' + id}
						isActive={activeId === id}
						hiddenRange={[undefined, EMediaQuery.md]}
					>
						<button
							data-cy={`navbar-main-button-${id}`}
							onClick={() => setMobileOpenId((prevState) => (prevState === id ? '' : id))}
						>
							<NGHeading
								as='p'
								fontFamily={EFontFamily.cinzel}
								type={ETextType.h3}
								textColor={isDisabled ? ETextColor.grey : activeId === id ? ETextColor.white : ETextColor.grey}
								shadowColor={activeId === id ? EThemeColor.white : undefined}
								textTransform={ETextTransform.uppercase}
								firstLetterFactor={1.2}
								singleLine={false}
							>
								{label}
							</NGHeading>

							<AnimatedArrow isActive={mobileOpenId === id} width={12.8} height={9.04} />
						</button>
						<Collapse isOpen={mobileOpenId === id} width={'100%'}>
							<Sublinks activeId={id} mobilePageId={id} onClick={onClick} hiddenRange={[undefined, EMediaQuery.md]} />
						</Collapse>
					</StyledMobileListItem>
				) : (
					<StyledNavLinksListItem
						key={href + '-' + id}
						isDisabled={isDisabled}
						hiddenRange={[EMediaQuery.md, undefined]}
						data-cy={`navbar-link-${id}`}
					>
						<NeutralButton onClick={() => handleClick(href)} disabled={isDisabled}>
							<NGText
								type={ETextType.p}
								size={ESize.m}
								textColor={isDisabled ? ETextColor.grey : activeId === id ? ETextColor.white : ETextColor.grey}
								shadowColor={activeId === id ? EThemeColor.white : undefined}
								textTransform={ETextTransform.uppercase}
								firstLetterFactor={1.2}
								fontFamily={EFontFamily.cinzel}
							>
								{label}
							</NGText>
						</NeutralButton>

						{!isDisabled && (
							<StyledSublinkList>
								<StyledSublinks>
									{Object.values(APP_SUB_MENUS?.[id as keyof typeof APP_SUB_MENUS] || { value: {} })?.map(
										(submenu: ISubmenu): ReactNode => {
											const { type, icon, label: submenuLabel, isDisabled: submenuDisabled, id: submenuId } = submenu;
											const color = getIconColor(submenuId, id);

											return (
												<FlexEx
													key={type}
													forwardedAs='li'
													vertical={EFlex.center}
													isDisabled={submenuDisabled}
													data-cy={`navbar-submenu-${type}`}
												>
													<NGIcon
														type={icon}
														size={ESize.s}
														color={submenuDisabled ? ETextColor.grey : color?.text || ETextColor.grey}
														shadowColor={color?.theme}
														paddingRight={ESize['3xs']}
													/>
													<NGText
														size={ESize.xs}
														textColor={submenuDisabled ? ETextColor.grey : color?.text || ETextColor.grey}
														shadowColor={color?.theme}
														textTransform={ETextTransform.uppercase}
														firstLetterFactor={1.2}
														fontFamily={EFontFamily.cinzel}
													>
														{submenuLabel}
													</NGText>
													<ItemButton
														data-cy={`navbar-submenu-${type}`}
														onClick={() => handleSubmenuClick(href, submenuId)}
														disabled={submenuDisabled}
													/>
												</FlexEx>
											);
										}
									)}
								</StyledSublinks>
								<StyledBorderContainer />
							</StyledSublinkList>
						)}
					</StyledNavLinksListItem>
				);
			})}

			{isSmallerThanMd ? (
				<StyledNavLinksListItem>
					<Flex
						direction={EFlex.column}
						horizontal={EFlex.center}
						vertical={EFlex.center}
						hiddenRange={[undefined, EMediaQuery.md]}
					>
						<NavbarAvatar />

						<NGHeading
							as='p'
							type={ETextType.h2}
							fontFamily={EFontFamily.cinzel}
							textAlign={ETextAlign.center}
							marginTop={ESize['4xs']}
							height={30}
						>
							{username}
						</NGHeading>
					</Flex>
				</StyledNavLinksListItem>
			) : null}
		</StyledNavLinksList>
	);
};

export { Mainlinks };
